// [2026-09-07] ADDED — an in-app dialog, replacing window.prompt() and window.confirm().
//
// WHY REPLACE THEM. These are drawn by the BROWSER, not by us. They say
// "dev.admin.v1.proctor.incruiter.com says", they cannot be styled, and they were the last thing an
// operator saw before granting real credits, suspending a live tenant, or revoking a key that
// working exams depend on. A dialog that looks like a browser error is the wrong frame for a
// decision that moves money.
//
// They are also SYNCHRONOUS AND BLOCKING — they freeze the event loop and stop React rendering —
// and in a sandboxed or embedded frame they can be suppressed entirely, in which case prompt()
// returns null and the action silently never happens. That failure looks like a dead button.
//
// ═══ ONE DIALOG, NOT TWO ════════════════════════════════════════════════════════════════════════
//
// The bigger win is not the styling. Three of these flows asked TWO questions in a row — grant
// asked for the amount, then for the reason; adjust and suspend did the same. Two stacked browser
// prompts means: answer, dialog vanishes, a second unrelated-looking dialog appears, and cancelling
// the second silently discards the first. Worse, the amount was validated only AFTER the second
// prompt was answered, so typing "abc" cost you two dialogs before a toast said it was wrong.
//
// `fields` takes as many inputs as the question needs, so one decision is one dialog, and
// `validate` runs INSIDE it — the error appears under the field that is wrong, while the value is
// still on screen and editable.
//
// ═══ THE API IS A PROMISE, on purpose ═══════════════════════════════════════════════════════════
//
// Every call site already lived inside an async function and read `const x = window.prompt(...)`,
// so `const values = await ask({...})` is the same control flow with the same early return. No
// callback pyramid, no restructuring of the mutation that follows.
//
//   const v = await ask({ title, fields: [...] })
//   if (!v) return                    // cancelled — exactly like `if (x === null) return`
//   await admin.grant(id, Number(v.credits), v.reason)
//
// With no `fields` it is a confirm: resolves `{}` (truthy) or null.

import { useCallback, useEffect, useRef, useState } from 'react'
import { Modal } from '@incruiter/proctoring-client-frontend'

/** Returns `[ask, dialog]`. Render `dialog` anywhere in the component's tree.
 *
 *  options: { title, message, confirmLabel, cancelLabel, tone: 'danger' | 'primary', fields }
 *  field:   { name, label, type, defaultValue, placeholder, hint, validate }
 *
 *  `validate` is given the raw string and returns an error message, or nothing if it is fine.
 */
export function useAsk() {
  const [pending, setPending] = useState(null)
  // Held in a ref as well as state so the unmount cleanup can settle a promise that is still open
  // without re-running on every render.
  const pendingRef = useRef(null)

  const ask = useCallback((options) => new Promise((resolve) => {
    const entry = { ...options, resolve }
    pendingRef.current = entry
    setPending(entry)
  }), [])

  // A dialog open when the screen unmounts (a 401 drops the shell, an operator navigates away)
  // would otherwise leave its promise forever unsettled, and the `await` after it never returns —
  // the action would hang rather than being cancelled. Resolving null is the safe answer: it means
  // "not confirmed", so nothing destructive proceeds.
  useEffect(() => () => {
    if (pendingRef.current) {
      pendingRef.current.resolve(null)
      pendingRef.current = null
    }
  }, [])

  const settle = useCallback((result) => {
    const entry = pendingRef.current
    pendingRef.current = null
    setPending(null)
    entry?.resolve(result)
  }, [])

  return [ask, pending ? <AskDialog options={pending} onSettle={settle} /> : null]
}

function AskDialog({ options, onSettle }) {
  const fields = options.fields || []
  const [values, setValues] = useState(() =>
    Object.fromEntries(fields.map((f) => [f.name, f.defaultValue ?? ''])))
  const [errors, setErrors] = useState({})
  const firstRef = useRef(null)
  const cancelRef = useRef(null)

  // FOCUS: the first input if there is one, otherwise CANCEL.
  //
  // Not the confirm button. A confirm-only dialog here is always destructive — revoking a key,
  // suspending a tenant — and focusing the confirming button means a stray Enter (from the very
  // keystroke that opened the dialog) carries the action out. Where there IS an input, focusing it
  // is right: the operator's next action is to type, and they cannot submit without doing so.
  useEffect(() => {
    if (firstRef.current) firstRef.current.select()
    else cancelRef.current?.focus()
  }, [])

  function submit(event) {
    event?.preventDefault()
    const found = {}
    for (const f of fields) {
      const message = f.validate?.(values[f.name])
      if (message) found[f.name] = message
    }
    setErrors(found)
    if (Object.keys(found).length) return
    onSettle(fields.length ? values : {})
  }

  const danger = options.tone !== 'primary'

  return (
    <Modal title={options.title} subtitle={options.subtitle} onClose={() => onSettle(null)}>
      {/* A FORM, so Enter submits and the browser's own validation semantics apply. The previous
          browser prompts got that for free; a div full of inputs would not, and losing it would
          make this feel slower than what it replaces. */}
      <form onSubmit={submit}>
        {options.message && (
          // `pre-line` so an existing message written with \n\n — which is how the window.confirm
          // strings were written — still paragraphs correctly.
          <div style={{ fontSize: 13.5, lineHeight: 1.6, color: 'var(--ink-secondary)',
                        whiteSpace: 'pre-line', marginBottom: fields.length ? 16 : 0 }}>
            {options.message}
          </div>
        )}

        {fields.map((f, i) => (
          <label className="field" key={f.name}>
            <span>{f.label}</span>
            <input
              ref={i === 0 ? firstRef : undefined}
              type={f.type || 'text'}
              value={values[f.name]}
              placeholder={f.placeholder}
              // aria-invalid and the id pairing are what make the error reach a screen reader;
              // colour alone would announce nothing.
              aria-invalid={errors[f.name] ? 'true' : undefined}
              aria-describedby={errors[f.name] ? `${f.name}-error` : (f.hint ? `${f.name}-hint` : undefined)}
              onChange={(e) => {
                setValues((v) => ({ ...v, [f.name]: e.target.value }))
                // Clear this field's error as soon as it is edited. Leaving it up while the person
                // is fixing it is a dialog arguing with someone who has already agreed.
                if (errors[f.name]) setErrors((prev) => ({ ...prev, [f.name]: undefined }))
              }}
            />
            {errors[f.name]
              ? <span id={`${f.name}-error`} style={{ color: 'var(--critical)', fontSize: 12.5 }}>
                  {errors[f.name]}
                </span>
              : f.hint && <span id={`${f.name}-hint`} className="muted" style={{ fontSize: 12.5 }}>
                  {f.hint}
                </span>}
          </label>
        ))}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
          <button type="button" ref={cancelRef} className="btn ghost"
                  onClick={() => onSettle(null)}>
            {options.cancelLabel || 'Cancel'}
          </button>
          <button type="submit" className="btn"
                  style={danger ? { background: 'var(--critical)', borderColor: 'var(--critical)' }
                                : undefined}>
            {options.confirmLabel || 'Confirm'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ── the validators the call sites share ───────────────────────────────────────────────
//
// Here rather than inline so "what counts as a valid credit amount" has ONE answer. They were
// previously three slightly different `Number.isFinite` checks written at three call sites, run
// after the dialog had already closed.

export const positiveNumber = (label = 'a positive number') => (raw) => {
  const n = Number(raw)
  if (!raw?.toString().trim()) return `Enter ${label}.`
  if (!Number.isFinite(n)) return 'That is not a number.'
  if (n <= 0) return `Enter ${label}.`
  return undefined
}

export const nonZeroNumber = (raw) => {
  const n = Number(raw)
  if (!raw?.toString().trim()) return 'Enter a number.'
  if (!Number.isFinite(n)) return 'That is not a number.'
  if (n === 0) return 'Zero would change nothing.'
  return undefined
}

export const required = (what = 'This') => (raw) =>
  (raw?.toString().trim() ? undefined : `${what} is required.`)
